import React, { useEffect, useImperativeHandle, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { datetimeFormats, getCurrencyPosition } from '@constants/masterData';
import { timeZoneOptions } from '@constants/timezone';
import useBasicForm from '@hooks/useBasicForm';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, NumericField, SelectField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { Col, Row } from 'antd';

const messages = defineMessages({
    validationSeparator: 'Group separator not same Decimal separator',
    validationMaxLength: 'Currency maximum string length is 3',
    stringContain: 'String not contain , and .',
    acceptValue: 'Accepts values from 0 to 9',
    timezone: 'Timezone',
    dateTimeFormat: 'Date time format',
    currency: 'Currency',
    decimalSeparator: 'Decimal separator',
    decimalSpace: 'Decimal Space',
    groupSeparator: 'Group separator',
    employee_view_all_tables: 'Employee view all tables',
    workload: 'Work load',
});
const GeneralForm = (props) => {
    const { formId, actions, onSubmit, setIsChangedFormValues, dataDetail, isCreateMode, formRef } = props;

    const { form, mixinFuncs, onValuesChange, setFieldValue, getFieldValue } = useBasicForm({
        onSubmit,
        setIsChangedFormValues,
    });

    useImperativeHandle(formRef, () => ({
        submit: () => {
            form.submit();
        },
    }));

    const [currencys, setCurrencys] = useState([]);
    const [data, setData] = useState();
    const [formState, setFormState] = useState({});
    const translate = useTranslate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const deviceId = queryParams.get('deviceId');

    useEffect(() => {
        if (dataDetail?.settings) {
            try {
                const parsedSettings = JSON.parse(dataDetail.settings);
                const generalSettings = parsedSettings;
                setFormState(generalSettings);
                form.setFieldsValue({
                    timezone: generalSettings?.timezone?.name,
                    currency: generalSettings?.currency,
                    currencyPosition: generalSettings?.currency_position,
                    decimalSeparator: generalSettings?.decimal_separator,
                    decimalSpace: generalSettings?.decimal_space,
                    groupSeparator: generalSettings?.group_separator,
                    dateTimeFormat: generalSettings?.date_time_format,
                    priceSetting: generalSettings?.price_setting,
                    prepareTime: generalSettings?.prepare_time,
                });
            } catch (error) {
                console.error('Error', error);
            }
        }
    }, [dataDetail, form]);

    const handleSubmitUpdate = (values) => {
        const parsedSettings = JSON.parse(dataDetail.settings);
        const formattedExpireDate = dataDetail?.expireDate ? `${dataDetail.expireDate} 00:00:00` : '';
        const selectedTimezone = timeZoneOptions.find((option) => option.value === values?.timezone);
        const updatedGeneral = {
            timezone: {
                name: values?.timezone,
                offset: selectedTimezone?.offset || '0:00',
            },
            currency: values?.currency,
            currency_position: values?.currencyPosition,
            decimal_separator: values?.decimalSeparator,
            decimal_space: values?.decimalSpace,
            group_separator: values?.groupSeparator,
            date_time_format: values?.dateTimeFormat,
            prepare_time: values?.prepareTime,
            mysql: {
                databaseHost: parsedSettings?.mysql?.databaseHost,
                databasePort: parsedSettings?.mysql?.databasePort,
                database: parsedSettings?.mysql?.database,
                username: parsedSettings?.mysql?.username,
                password: parsedSettings?.mysql?.password,
            },
        };
        const settings = JSON.stringify(updatedGeneral);

        const payload = {
            id: parseInt(deviceId),
            customerId: dataDetail?.customer?.id || 0,
            deviceId: dataDetail?.deviceId,
            expireDate: formattedExpireDate,
            ipSocket: dataDetail?.ipSocket,
            name: dataDetail?.name,
            portSocket: dataDetail?.portSocket,
            settings: settings,
        };

        return onSubmit(payload);
    };

    useEffect(() => {
        if (data) {
            setFormState({ ...data });
            form.setFieldsValue({
                timezone: data?.timezone?.name,
                currency: data?.currency,
                currencyPosition: data?.currency_position,
                decimalSeparator: data?.decimal_separator,
                decimalSpace: data?.decimal_space,
                groupSeparator: data?.group_separator,
                dateTimeFormat: data?.date_time_format,
                group_position: data?.groupPosition,
                priceSetting: data?.price_setting,
            });
        }
    }, [data]);

    const handleFormChange = (changedValues, allValues) => {
        onValuesChange();
        setFormState((prevState) => ({
            ...prevState,
            ...changedValues,
        }));
    };

    useEffect(() => {
        const defaultCurrency = formState.currency || data?.currency || '$';
        const defaultGroupSeparator = formState.groupSeparator || data?.group_separator || '.';
        const defaultDecimalSeparator = formState.decimalSeparator || data?.decimal_separator || ',';
        const decimalSpace = formState.decimalSpace || data?.decimal_space || 2;

        const updatedCurrencys = getCurrencyPosition(
            defaultCurrency,
            defaultGroupSeparator,
            defaultDecimalSeparator,
            decimalSpace,
        );
        setCurrencys(updatedCurrencys);
    }, [formState]);
    return (
        <BaseForm
            id={formId}
            onFinish={handleSubmitUpdate}
            form={form}
            layout="vertical"
            onValuesChange={handleFormChange}
        >
            <Row gutter={16}>
                <Col span={12}>
                    <SelectField
                        label={translate.formatMessage(messages.timezone)}
                        name="timezone"
                        options={timeZoneOptions}
                        form={form}
                        value={form.getFieldValue('timezone')}
                        onChange={(value) => {
                            const selectedOption = timeZoneOptions.find((option) => option.value === value);

                            if (selectedOption) {
                                form.setFieldsValue({
                                    valueData: selectedOption.value,
                                    offset: selectedOption.offset,
                                });
                            }
                        }}
                    />
                </Col>
                <Col span={12}>
                    <SelectField
                        label={translate.formatMessage(messages.dateTimeFormat)}
                        name="dateTimeFormat"
                        options={datetimeFormats}
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        label={translate.formatMessage(messages.currency)}
                        name="currency"
                        rules={[
                            {
                                pattern: /^[^,.]*$/,
                                message: translate.formatMessage(messages.stringContain),
                            },
                            {
                                max: 3,
                                message: translate.formatMessage(messages.validationMaxLength),
                            },
                        ]}
                    />
                </Col>
                <Col span={12}>
                    <SelectField
                        required
                        label={translate.formatMessage(commonMessage.currencyPosition)}
                        name="currencyPosition"
                        options={currencys}
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <TextField
                        label={translate.formatMessage(messages.decimalSeparator)}
                        name="decimalSeparator"
                        rules={[
                            ({ getFieldValue }) => ({
                                validator(rule, value) {
                                    const groupSeparator = getFieldValue('groupSeparator');
                                    if (groupSeparator == value) {
                                        return Promise.reject();
                                    }

                                    return Promise.resolve();
                                },
                            }),
                        ]}
                        dependencies={'groupSeparator'}
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        label={translate.formatMessage(messages.decimalSpace)}
                        name="decimalSpace"
                        rules={[
                            {
                                max: 1,
                                message: '',
                            },
                            {
                                pattern: /^[0-9]+$/,
                                message: '',
                            },
                        ]}
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        label={translate.formatMessage(messages.groupSeparator)}
                        name="groupSeparator"
                        rules={[
                            ({ getFieldValue }) => ({
                                validator(rule, value) {
                                    const decimalSeparator = getFieldValue('decimalSeparator');

                                    if (decimalSeparator == value) {
                                        return Promise.reject();
                                    }

                                    return Promise.resolve();
                                },
                            }),
                        ]}
                        dependencies={'decimalSeparator'}
                    />
                </Col>
                <Col span={12}>
                    <NumericField
                        formatter={(v) => v}
                        parser={(v) => v}
                        label={translate.formatMessage(commonMessage.prepareTime)}
                        name="prepareTime"
                        rules={[
                            {
                                pattern: /^[0-9]+$/,
                                message: '',
                            },
                        ]}
                        width="100%"
                    />
                </Col>
            </Row>
        </BaseForm>
    );
};

export default GeneralForm;
